import React, { useState, useEffect } from 'react';
import { getLocalPrice } from '../utils/displayCurrency';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Heart, CreditCard, Package, Loader2, CheckCircle, AlertCircle, Gift, Send, Users } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useAdultMode } from '../hooks/useAdultMode';
import { isPlayBillingAvailable, purchaseHeartsPack } from '../utils/googlePlayBilling';

const HeartsStore = () => {
  const { user } = useAuth();
  const isAdultMode = useAdultMode();
  const isTeenAccount = (
    (user?.role || '').toLowerCase() === 'teen'
    || (user?.age_band || '').toLowerCase() === 'teen'
    || (Number(user?.age) > 0 && Number(user?.age) < 18)
  );
  // Pay-as-you-go (Oct 2026): FIRST STEPS teens can buy Hearts with their
  // own card — no parent subscription needed. Challenges earn badges only.
  const isPaygTeen = isTeenAccount && ((user?.subscription_tier || 'free').toLowerCase() === 'free');
  const [cardConfirmed, setCardConfirmed] = useState(false);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [askingParent, setAskingParent] = useState(null);   // pack_id currently being sent for approval
  const [pendingApproval, setPendingApproval] = useState(null); // { request_id, pack_id, parent_email_masked, expires_at }
  const [result, setResult] = useState(null);
  const [heartsBalance, setHeartsBalance] = useState(user?.hearts_balance || 0);
  const [voiceMinutes, setVoiceMinutes] = useState(null);
  const [showGifting, setShowGifting] = useState(false);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftHearts, setGiftHearts] = useState(50);
  const [giftMessage, setGiftMessage] = useState('');
  const [gifting, setGifting] = useState(false);
  const [giftResult, setGiftResult] = useState(null);

  useEffect(() => {
    apiClient.request('/api/billing/hearts-packs')
      .then(data => setPacks(data.packs || []))
      .catch(() => setPacks([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    apiClient.request('/api/users/me')
      .then(u => setHeartsBalance(u.hearts_balance || 0))
      .catch(() => {});
    apiClient.request('/api/billing/voice-minutes')
      .then(v => setVoiceMinutes(v.remaining_minutes))
      .catch(() => {});
  }, [result]);

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    setResult(null);
    try {
      // On Android native, route through Google Play Billing (required by Play policy).
      if (isPlayBillingAvailable()) {
        const res = await purchaseHeartsPack(packId);
        if (res.status === 'credited') {
          setResult({ type: 'success', message: res.message });
        } else if (res.status === 'cancelled') {
          setResult({ type: 'error', message: 'Purchase cancelled' });
        } else {
          setResult({ type: 'error', message: res.message || 'Purchase failed' });
        }
      } else {
        // Web → Stripe Checkout (hosted). Backend resolves the pack price
        // server-side and creates a Stripe Checkout Session; we just redirect.
        const res = await apiClient.request('/api/billing/checkout/hearts', {
          method: 'POST',
          body: JSON.stringify({
            pack_id: packId,
            origin_url: window.location.origin,
            card_confirmed: cardConfirmed,
          }),
        });
        if (res.url) {
          window.location.href = res.url;
        } else {
          setResult({ type: 'error', message: 'Could not start checkout. Please try again.' });
        }
      }
    } catch (err) {
      setResult({ type: 'error', message: err.message || 'Purchase failed' });
    } finally {
      setPurchasing(null);
    }
  };

  const handleGift = async () => {
    if (!giftEmail || giftHearts < 10) return;
    setGifting(true);
    setGiftResult(null);
    try {
      const res = await apiClient.request('/api/enhancements/gift-hearts', {
        method: 'POST',
        body: JSON.stringify({ recipient_email: giftEmail, hearts: giftHearts, message: giftMessage }),
      });
      setGiftResult({ type: 'success', message: res.message || `${res.hearts_sent} Hearts sent to ${res.recipient_name}!` });
      setHeartsBalance(res.new_balance);
      setGiftEmail(''); setGiftHearts(50); setGiftMessage('');
    } catch (err) {
      setGiftResult({ type: 'error', message: err.message || 'Gift failed' });
    }
    setGifting(false);
  };

  const handleAskParent = async (packId) => {
    setAskingParent(packId);
    setResult(null);
    try {
      const res = await apiClient.request('/api/billing/hearts-approval/request', {
        method: 'POST',
        body: JSON.stringify({ pack_id: packId, origin_url: window.location.origin }),
      });
      setPendingApproval({
        request_id: res.request_id,
        pack_id: packId,
        parent_email_masked: res.parent_email_masked,
        expires_at: res.expires_at,
      });
      setResult({ type: 'success', message: `Sent! Your parent will get an SMS + email in a moment.` });
    } catch (err) {
      setResult({ type: 'error', message: err?.message || 'Could not send the approval request.' });
    } finally {
      setAskingParent(null);
    }
  };

  // Poll the pending approval every 5s so the teen sees the live status flip.
  useEffect(() => {
    if (!pendingApproval?.request_id) return undefined;
    const poll = setInterval(async () => {
      try {
        const res = await apiClient.request(`/api/billing/hearts-approval/status/${pendingApproval.request_id}`);
        if (res.status === 'approved') {
          clearInterval(poll);
          setResult({ type: 'success', message: 'Parent approved! Hearts will land as soon as they finish the payment.' });
          setPendingApproval(null);
        } else if (res.status === 'declined') {
          clearInterval(poll);
          setResult({ type: 'error', message: 'Your parent declined this time. No worries — you can earn Hearts via streaks too!' });
          setPendingApproval(null);
        } else if (res.status === 'expired') {
          clearInterval(poll);
          setResult({ type: 'error', message: 'Request expired before your parent saw it. Try again.' });
          setPendingApproval(null);
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(poll);
  }, [pendingApproval]);

  const PACK_KEY = { 50: 'pack_small', 200: 'pack_medium', 500: 'pack_large' };
  const formatPrice = (pricePence, currency, hearts) => {
    const key = PACK_KEY[hearts];
    if (key) return getLocalPrice(key).text;
    return `\u00A3${(pricePence / 100).toFixed(2)}`;
  };

  const packIcons = [Heart, Package, CreditCard];
  const packArt = ['/packs/spark.png', '/packs/growth.png', '/packs/champion.png'];
  const packColors = [
    { bg: 'bg-purple-100', text: 'text-purple-500', btn: 'bg-purple-600 hover:bg-purple-700' },
    { bg: 'bg-purple-100', text: 'text-purple-500', btn: 'bg-purple-600 hover:bg-purple-700' },
    { bg: 'bg-orange-100', text: 'text-orange-600', btn: 'bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600' },
  ];

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="hearts-store-title">{isAdultMode ? 'Pro Packs' : 'Hearts Store'}</h1>
        <p className="text-gray-600 mb-4">Buy Hearts — your time credits for AvA conversations, plus streak freezes, gifts and collectibles</p>

        {/* Current Balance */}
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl flex items-center gap-3" data-testid="hearts-balance">
          <Heart size={24} className="text-pink-500" fill="currentColor" />
          <div>
            <p className="text-sm text-gray-500">Your Balance</p>
            <p className="text-2xl font-bold text-gray-900">{heartsBalance.toLocaleString()} Hearts</p>
            {voiceMinutes !== null && (
              <p className="text-xs text-purple-600 font-semibold mt-0.5" data-testid="voice-minutes-balance">
                {voiceMinutes} voice minutes left this month
              </p>
            )}
          </div>
        </div>

        {/* Pay-as-you-go banner — FIRST STEPS teens only */}
        {isPaygTeen && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="payg-banner">
            <p className="font-semibold text-gray-900 mb-1">Pay as you go — your card, your choice</p>
            <p className="text-sm text-gray-600 mb-3">
              Buy Heart time credits whenever you need them — no subscription required.
              Heads up: on pay-as-you-go, challenges earn <span className="font-semibold">virtual badges only</span>.
            </p>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={cardConfirmed}
                onChange={(e) => setCardConfirmed(e.target.checked)}
                className="rounded w-4 h-4"
                data-testid="payg-card-confirm"
              />
              It&apos;s my card
            </label>
          </div>
        )}

        {/* Result Banner */}
        {result && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${result.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="purchase-result">
            {result.type === 'success' ? <CheckCircle size={20} className="text-green-600" /> : <AlertCircle size={20} className="text-red-600" />}
            <p className={`font-medium ${result.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{result.message}</p>
          </div>
        )}

        {/* Hearts Tiers */}
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="hearts-loading">
            <Loader2 size={32} className="animate-spin text-purple-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs.map((pack, i) => {
              const Icon = packIcons[i] || Heart;
              const colors = packColors[i] || packColors[0];
              const isBestValue = i === 1;
              return (
                <div
                  key={pack.id}
                  className={`p-6 bg-white rounded-xl shadow-sm ${isBestValue ? 'border-2 border-purple-500 shadow-lg relative' : 'border border-gray-200'}`}
                  data-testid={`hearts-tier-${pack.id}`}
                >
                  {isBestValue && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      BEST VALUE
                    </div>
                  )}
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">
                      <img src={packArt[i]} alt={pack.name} className="w-24 h-24 object-contain" data-testid={`pack-art-${pack.id}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pack.name}</h3>
                    <p className={`text-3xl font-bold mb-4 ${i === 2 ? 'text-orange-600' : 'text-purple-600'}`}>
                      {formatPrice(pack.price, pack.currency, pack.hearts)}
                    </p>
                    {i === 1 && <p className="text-sm text-green-600 font-semibold mb-4">Save 20%</p>}
                    {i === 2 && <p className="text-sm text-green-600 font-semibold mb-4">Save 30%</p>}
                    {/* Direct purchase: adults/parents always; teens only via
                        pay-as-you-go after ticking "It's my card" (server
                        enforces both the tick and the free-tier rule). */}
                    {(!isTeenAccount || isPaygTeen) && (
                    <button
                      onClick={() => handlePurchase(pack.id)}
                      disabled={purchasing === pack.id || askingParent === pack.id || pendingApproval?.pack_id === pack.id || (isPaygTeen && !cardConfirmed)}
                      title={isPaygTeen && !cardConfirmed ? 'Tick "It\'s my card" above first' : undefined}
                      className={`w-full py-2 px-4 ${colors.btn} text-white rounded-lg font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2`}
                      data-testid={`purchase-${pack.id}-hearts`}
                    >
                      {purchasing === pack.id ? (
                        <><Loader2 size={16} className="animate-spin" /> Processing...</>
                      ) : (
                        'Purchase'
                      )}
                    </button>
                    )}
                    {/* Sprint 194 — One-tap parent approval. Friction-killer
                        for impulse purchases; preserves parental control. */}
                    <button
                      onClick={() => handleAskParent(pack.id)}
                      disabled={askingParent === pack.id || pendingApproval?.pack_id === pack.id || purchasing === pack.id}
                      className="w-full py-2 px-4 mt-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2 border"
                      style={{ background: '#FFF', color: '#7B4FD8', borderColor: '#7B4FD840' }}
                      data-testid={`ask-parent-${pack.id}`}
                    >
                      {askingParent === pack.id ? (
                        <><Loader2 size={14} className="animate-spin" /> Sending…</>
                      ) : pendingApproval?.pack_id === pack.id ? (
                        <>Waiting for parent…</>
                      ) : (
                        <><Users size={14} /> Ask my parent</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* What Hearts Can Do — verified against live code paths. Hearts
            do NOT expand saved practice-partner count (that's plan-only —
            see app/constants/plans.py + api/avas.py). They DO buy the
            features below. Keep this list honest: marketing copy was
            previously misleading. */}
        <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What Can You Do with Hearts?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Heart size={20} className="text-purple-500 flex-shrink-0 mt-1" fill="currentColor" />
              <div>
                <p className="font-semibold text-gray-900">Claim Limited-Edition NFT Drops</p>
                <p className="text-sm text-gray-600">Weekly rare and legendary collectibles for your Vault (50–200 Hearts each)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart size={20} className="text-purple-500 flex-shrink-0 mt-1" fill="currentColor" />
              <div>
                <p className="font-semibold text-gray-900">Freeze a Slipping Streak</p>
                <p className="text-sm text-gray-600">Spend 50 Hearts to protect your daily-practice streak on a tough day</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart size={20} className="text-purple-500 flex-shrink-0 mt-1" fill="currentColor" />
              <div>
                <p className="font-semibold text-gray-900">Cheer On a Friend</p>
                <p className="text-sm text-gray-600">Stake Hearts on a friend&apos;s weekly goal — they win, you both win</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart size={20} className="text-purple-500 flex-shrink-0 mt-1" fill="currentColor" />
              <div>
                <p className="font-semibold text-gray-900">Gift Hearts to Anyone on HiBFF</p>
                <p className="text-sm text-gray-600">Send Hearts to a friend or family member as a boost — see the form below</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Hearts add flexible extras alongside your plan. They don&apos;t change how many practice partners you can save — that&apos;s set by your plan tier (Entry 3, Challenger 8, The Elite 20, Master 38).
          </p>
        </div>

        {/* Gift Hearts Section */}
        <div className="mt-8 p-6 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border border-pink-200" data-testid="hearts-gifting-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Gift size={24} className="text-pink-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Gift Hearts</h2>
                <p className="text-sm text-gray-600">Share the love — send Hearts to a friend or family member</p>
              </div>
            </div>
            <button
              onClick={() => setShowGifting(!showGifting)}
              className="px-4 py-2 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
              data-testid="toggle-gift-form"
            >
              {showGifting ? 'Close' : 'Send a Gift'}
            </button>
          </div>

          {showGifting && (
            <div className="space-y-3 pt-2">
              {giftResult && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${giftResult.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="gift-result">
                  {giftResult.type === 'success' ? <CheckCircle size={16} className="text-green-600" /> : <AlertCircle size={16} className="text-red-600" />}
                  <p className={`text-sm font-medium ${giftResult.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{giftResult.message}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="email"
                  value={giftEmail}
                  onChange={(e) => setGiftEmail(e.target.value)}
                  placeholder="Friend's email"
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent text-sm"
                  data-testid="gift-email"
                />
                <select
                  value={giftHearts}
                  onChange={(e) => setGiftHearts(Number(e.target.value))}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent text-sm"
                  data-testid="gift-amount"
                >
                  <option value={10}>10 Hearts</option>
                  <option value={25}>25 Hearts</option>
                  <option value={50}>50 Hearts</option>
                  <option value={100}>100 Hearts</option>
                  <option value={500}>500 Hearts</option>
                </select>
                <input
                  type="text"
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Personal message (optional)"
                  className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-300 focus:border-transparent text-sm"
                  data-testid="gift-message"
                />
              </div>
              <button
                onClick={handleGift}
                disabled={!giftEmail || gifting}
                className="w-full sm:w-auto px-6 py-2.5 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-gift-btn"
              >
                {gifting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Gift</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HeartsStore;
